import React, { useEffect, useState } from 'react'
import './BoostPromoStations.scss'
import { HorizontalLoader, ModalWrapper } from 'Components'
import { boostPromoStationBXService } from '../../../../../../BoostXpressAdmin/Services/boostPromoStationBXService'
import { BoostPromoStation } from 'BoostXpressAdmin/Model/boostPromoStationXModel'
import { handleError, handleSuccess } from 'Helpers'
import fillingStationsService, { FillingStation } from '../../../../../../BoostXpressAdmin/Services/fillingStationsService'
import { SubmitHandler, useForm } from 'react-hook-form'
import { ToastContainer } from 'react-toastify'

const BoostPromoStations = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [stations, setStations] = useState<BoostPromoStation[]>([])
  const [allStations, setAllStations] = useState<FillingStation[]>([])
  const [allStationsLoading, setAllStationsLoading] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [selectedStationId, setSelectedStationId] = useState('')

  const { register, handleSubmit, reset } = useForm({
    defaultValues: {
      stationId: '',
    },
  })

  const loadPromoStations = async () => {
    setIsLoading(true)
    try {
      const res = await boostPromoStationBXService.index()
      setStations(res.data?.data ?? [])
    } catch (error: any) {
      handleError(error)
      setStations([])
    } finally {
      setIsLoading(false)
    }
  }

  const loadAllStations = async () => {
    setAllStationsLoading(true)
    try {
      const res = await fillingStationsService.list({ pageNumber: 1 })
      setAllStations(res.data?.dataset ?? [])
    } catch (error: any) {
      handleError(error)
      setAllStations([])
    } finally {
      setAllStationsLoading(false)
    }
  }

  useEffect(() => {
    loadPromoStations()
    loadAllStations()
  }, [])

  const handleAddStation: SubmitHandler<{ stationId: string }> = async (data) => {
    if (!data.stationId) {
      handleError({ response: { data: { error: 'Please select a station.' } } })
      return
    }

    setIsSubmitting(true)
    try {
      await boostPromoStationBXService.addStation(data.stationId)
      handleSuccess('Station added to promo list successfully.')
      setShowAddModal(false)
      reset()
      setSelectedStationId('')
      await loadPromoStations()
    } catch (error: any) {
      handleError(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleRemoveStation = async (stationId: string) => {
    if (!window.confirm('Are you sure you want to remove this station from the promo list?')) {
      return
    }

    try {
      await boostPromoStationBXService.removeStation(stationId)
      handleSuccess('Station removed from promo list successfully.')
      await loadPromoStations()
    } catch (error: any) {
      handleError(error)
    }
  }

  const resetAddModal = () => {
    setShowAddModal(false)
    reset()
    setSelectedStationId('')
  }

  if (isLoading) {
    return <HorizontalLoader />
  }

  return (
    <div className='bx-promo-stations'>
      <div className='bx-promo-stations__header'>
          <div>
            <h2>Boost Promo Stations</h2>
            <div className='header-description'>Here is a list of stations that are currently enabled for promotions.</div>
          </div>
           <button
          type='button'
          className='bx-promo-stations__add-btn'
          onClick={() => setShowAddModal(true)}
          disabled={allStationsLoading}
        >
          Add Station
        </button>
     
      
      </div>

      <div className='bx-promo-stations__table-wrapper'>
        <table className='bx-promo-stations__table'>
          <thead>
            <tr>
              <th>S/N</th>
              <th>Business Name</th>
              <th>Branch Name</th>
              <th>Address</th>
              <th>City</th>
              <th>State</th> 
              <th>Products</th>
              <th>Is Public</th> 
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={8} className='text-center'>
                  Loading...
                </td>
              </tr>
            ) : stations.length === 0 ? (
              <tr>
                <td colSpan={8} className='text-center'>
                  No promo stations found
                </td>
              </tr>
            ) : (
              stations.map((station, index) => (
                <tr key={station.resourceId}>
                  <td>{index + 1}</td>
                  <td>{station.businessName}</td>
                  <td>{station.branchName}</td>
                  <td>{station.address}</td>
                  <td>{station.city}</td>
                  <td>{station.state}</td>
                   
                  <td>{station.products?.map((p) => p.product).join(', ') || '-'}</td>
                  <td>{station.isPublic ? 'Yes' : 'No'}</td>
                  <td>
                    <button
                      type='button'
                      className='bx-promo-stations__delete-btn'
                      onClick={() => handleRemoveStation(station.resourceId)}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showAddModal && (
        <ModalWrapper
          showModal={showAddModal}
          closeModal={resetAddModal}
          title='Add Station to Promo List'
          hideHeader={false}
        >
          <form className='bx-promo-stations__modal' onSubmit={handleSubmit(handleAddStation)}>
            <div className='bx-promo-stations__field mb-3 mt-4'>
              <label className='bx-promo-stations__label'>Select Station</label>
              <select
                {...register('stationId', { required: true })}
                className='form-control'
                disabled={allStationsLoading || isSubmitting}
              >
                <option value=''>Select a station...</option>
                {allStations.map((station) => (
                  <option key={station.id} value={station.id}>
                    {station.businessName} - {station.branchName} ({station.city}, {station.state})
                  </option>
                ))}
              </select>
            </div>

            <div className='bx-promo-stations__modal-actions'>
              <button type='submit' className='btn btn-default w-100' disabled={isSubmitting}>
                {isSubmitting ? <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true'></span> : 'Add Station'}
              </button>
            </div>
          </form>
        </ModalWrapper>
      )}

      <ToastContainer />
    </div>
  )
}

export default BoostPromoStations
